// JobPeek — "what IS this job?", answered where you are, without losing it.
//
// Owner, 2026-09-17: "MAKE THE FUCKING JOBS CLICKABLE EVERYWHERE!!! I keep
// going to approve time sheets but want to be able to click on a job a modal
// to open up so I can see what the fuck the job is!"
//
// ── Why a modal and not a link ─────────────────────────────────────────────
//
// apps/shared/jobLink.js already exists and navigates to the Office record —
// that is right on the live board, where you tap a finished shift and go look
// at it. It is WRONG here. Approving a timesheet is a task you are in the
// middle of: half-read lines, an unsaved reason, a scroll position in a week.
// Navigating away to answer "which job is this?" and coming back is why the
// question goes unanswered instead.
//
// So this opens over the screen and closes back onto it. Nothing is lost
// because nothing moved.
//
// ── It fetches NOTHING ─────────────────────────────────────────────────────
//
// Every field below already arrives with the row. hoursModel.sheetLines joins
// jobs, clients and properties and returns job_title, job_description,
// client_name, property_name, property_address, property_city,
// job_created_at, job_completed_at, job_pay_type and job_pay_amount.
//
// That matters for more than speed: a modal that fetches has a loading state,
// an error state and a way to be empty, and all three would be new ways for
// this to fail on a phone in a yard. A modal that reads what the caller
// already has cannot fail at all. If a field is missing the row did not carry
// it, and the modal says so rather than showing a blank.
//
// ── What it deliberately does not do ───────────────────────────────────────
//
// No editing. This answers a question; it does not become a second place to
// change a job, which would be a second place to get a job wrong. The link to
// the full record is at the bottom for anyone who does want to edit — and it
// is the ONLY navigation in here, so leaving is always deliberate.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, FwButton, Pill } from '../../ui/fw';
import { jobRecordHref } from './jobLink';
import './JobPeek.css';

/** One labelled fact. Renders nothing at all when the row did not carry it. */
function Fact({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="jp-peek__fact">
      <span className="jp-peek__label">{label}</span>
      <span className="jp-peek__value">{children}</span>
    </div>
  );
}

/**
 * Read a job out of whatever row the caller has.
 *
 * Sheet lines prefix the joined columns (`job_title`, `property_name`), the
 * board carries a bare job (`title`, `property_name`). Both are accepted so a
 * caller never has to reshape a row to ask this question — the reason the
 * board's own "clickable" work did not get reused here.
 */
export function jobFromRow(row) {
  if (!row) return null;
  const id = row.job_id != null ? row.job_id : row.id;
  if (id == null) return null;
  return {
    id,
    title: row.job_title || row.title || null,
    description: row.job_description || row.description || null,
    client: row.client_name || null,
    property: row.property_name || null,
    address: [row.property_address, row.property_city].filter(Boolean).join(', ') || null,
    createdAt: row.job_created_at || row.created_at || null,
    completedAt: row.job_completed_at || row.completed_at || null,
    payType: row.job_pay_type || row.pay_type || null,
    payAmount: row.job_pay_amount != null ? row.job_pay_amount : row.pay_amount,
  };
}

const dayText = (iso) => {
  if (!iso) return null;
  const d = new Date(String(iso).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * @param {object}   props
 * @param {object}   props.job      from jobFromRow
 * @param {Function} props.onClose
 */
export function JobPeek({ job, onClose }) {
  const navigate = useNavigate();
  if (!job) return null;
  const title = job.title || `Job #${job.id}`;
  // onClick + navigate, matching Now.js — FwButton takes no href, and
  // jobRecordHref returns '' for a missing id rather than a broken link, so an
  // unopenable job gets no button instead of a button that goes nowhere.
  const href = jobRecordHref(job.id);
  return (
    <Sheet
      open
      title={title}
      onClose={onClose}
      data-job-peek
      footer={href ? (
        <FwButton
          full
          size="lg"
          variant="ghost"
          onClick={() => navigate(href)}
          data-job-peek-open
        >
          Open the full record
        </FwButton>
      ) : null}
    >
      <div className="jp-peek">
        {job.completedAt ? <Pill tone="good">Finished {dayText(job.completedAt)}</Pill> : null}

        <Fact label="Client">{job.client}</Fact>
        <Fact label="Property">{job.property}</Fact>
        <Fact label="Address">{job.address}</Fact>
        <Fact label="Scheduled">{dayText(job.createdAt)}</Fact>

        {/* The one field people actually open this for, and the one most
            likely to be long. It gets its own block rather than a row, so it
            wraps instead of squeezing the label off a 320px screen. */}
        {job.description ? (
          <div className="jp-peek__desc">
            <span className="jp-peek__label">What the job is</span>
            <p className="jp-peek__text">{job.description}</p>
          </div>
        ) : null}

        {/* Said out loud rather than shown as a blank. A modal that renders
            nothing reads as broken; one that says the row is thin reads as
            honest, and tells you the record is where to look. */}
        {!job.client && !job.property && !job.description ? (
          <p className="jp-peek__text jp-peek__muted">
            This line carries only the job&apos;s name. Open the full record for the rest.
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}

export default JobPeek;
